import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const YEAR_MIN = 1920;
const YEAR_MAX = 2030;
const ITEM_HEIGHT = 40;
const VISIBLE_ITEMS = 5;

function getDaysInMonth(month: number, year?: number): number {
  // month is 0-indexed
  const y = year ?? 2000; // use leap year if no year specified
  return new Date(y, month + 1, 0).getDate();
}

interface ScrollColumnProps {
  items: { value: number | string; label: string }[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  disabled?: boolean;
}

const ScrollColumn = ({ items, selectedIndex, onSelect, disabled }: ScrollColumnProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const scrollToIndex = useCallback((index: number, smooth = true) => {
    if (!containerRef.current) return;
    const scrollTop = index * ITEM_HEIGHT;
    containerRef.current.scrollTo({
      top: scrollTop,
      behavior: smooth ? "smooth" : "auto",
    });
  }, []);

  useEffect(() => {
    // On mount or when selectedIndex changes externally, scroll to it
    if (!isScrollingRef.current) {
      scrollToIndex(selectedIndex, false);
    }
  }, [selectedIndex, scrollToIndex]);

  const handleScroll = () => {
    if (!containerRef.current) return;
    isScrollingRef.current = true;

    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      if (!containerRef.current) return;
      const scrollTop = containerRef.current.scrollTop;
      const index = Math.round(scrollTop / ITEM_HEIGHT);
      const clampedIndex = Math.max(0, Math.min(items.length - 1, index));
      scrollToIndex(clampedIndex, true);
      onSelect(clampedIndex);
      isScrollingRef.current = false;
    }, 80);
  };

  // Padding so first/last items can be centered
  const paddingItems = Math.floor(VISIBLE_ITEMS / 2);

  return (
    <div className="relative flex-1">
      {/* Highlight bar */}
      <div
        className="pointer-events-none absolute left-0 right-0 z-10 rounded-lg"
        style={{
          top: paddingItems * ITEM_HEIGHT,
          height: ITEM_HEIGHT,
          backgroundColor: disabled ? "transparent" : "hsl(43 50% 54% / 0.15)",
        }}
      />
      {/* Fade edges */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-20 h-16 bg-gradient-to-b from-white to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-16 bg-gradient-to-t from-white to-transparent" />

      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="overflow-y-auto scrollbar-hide touch-pan-y"
        style={{
          height: VISIBLE_ITEMS * ITEM_HEIGHT,
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
        }}
      >
        {/* Top padding */}
        {Array.from({ length: paddingItems }).map((_, i) => (
          <div key={`pad-top-${i}`} style={{ height: ITEM_HEIGHT }} />
        ))}

        {items.map((item, index) => {
          const isSelected = index === selectedIndex;
          return (
            <div
              key={`${item.value}-${index}`}
              onClick={() => {
                if (disabled) return;
                onSelect(index);
                scrollToIndex(index);
              }}
              className="flex items-center justify-center cursor-pointer select-none"
              style={{
                height: ITEM_HEIGHT,
              }}
            >
              <span
                className={
                  disabled
                    ? "text-sm text-muted-foreground/40"
                    : isSelected
                    ? "text-lg font-bold text-[hsl(0_0%_10%)]"
                    : "text-sm text-[hsl(0_0%_40%)] opacity-60"
                }
              >
                {item.label}
              </span>
            </div>
          );
        })}

        {/* Bottom padding */}
        {Array.from({ length: paddingItems }).map((_, i) => (
          <div key={`pad-bottom-${i}`} style={{ height: ITEM_HEIGHT }} />
        ))}
      </div>
    </div>
  );
};

interface DateScrollPickerProps {
  value?: Date;
  onChange: (date: Date, yearSelected: boolean) => void;
  yearOptional?: boolean;
  yearRequired?: boolean;
  onDone?: () => void;
}

const DateScrollPicker = ({
  value,
  onChange,
  yearOptional = false,
  yearRequired = false,
  onDone,
}: DateScrollPickerProps) => {
  const today = new Date();
  const initialDay = value ? value.getDate() : today.getDate();
  const initialMonth = value ? value.getMonth() : today.getMonth();
  const initialYear = value ? value.getFullYear() : today.getFullYear();
  const isDefaultYear = value ? (value.getFullYear() === 2000) : true;

  const [selectedDay, setSelectedDay] = useState(initialDay);
  const [selectedMonth, setSelectedMonth] = useState(initialMonth);
  const [selectedYearIndex, setSelectedYearIndex] = useState<number>(() => {
    if (yearOptional && isDefaultYear) {
      // Default to "Any" (index 0) for optional year fields with no saved date
      return 0;
    }
    const yearVal = value ? value.getFullYear() : today.getFullYear();
    const idx = yearOptional ? yearVal - YEAR_MIN + 1 : yearVal - YEAR_MIN;
    return Math.max(0, idx);
  });

  const [yearSelected, setYearSelected] = useState(!isDefaultYear && !yearOptional);

  // Build items
  const dayItems = Array.from({ length: 31 }, (_, i) => ({
    value: i + 1,
    label: String(i + 1).padStart(2, "0"),
  }));

  const monthItems = MONTHS.map((m, i) => ({ value: i, label: m }));

  const yearItems = yearOptional
    ? [{ value: "any", label: "Any" }, ...Array.from({ length: YEAR_MAX - YEAR_MIN + 1 }, (_, i) => ({
        value: YEAR_MIN + i,
        label: String(YEAR_MIN + i),
      }))]
    : Array.from({ length: YEAR_MAX - YEAR_MIN + 1 }, (_, i) => ({
        value: YEAR_MIN + i,
        label: String(YEAR_MIN + i),
      }));

  // Auto-snap day when month/year changes
  useEffect(() => {
    const currentYear = yearOptional
      ? (selectedYearIndex === 0 ? undefined : YEAR_MIN + selectedYearIndex - 1)
      : YEAR_MIN + selectedYearIndex;
    const maxDay = getDaysInMonth(selectedMonth, currentYear);
    if (selectedDay > maxDay) {
      setSelectedDay(maxDay);
    }
  }, [selectedMonth, selectedYearIndex, yearOptional]);

  // Emit changes
  useEffect(() => {
    const currentYear = yearOptional
      ? (selectedYearIndex === 0 ? 2000 : YEAR_MIN + selectedYearIndex - 1)
      : YEAR_MIN + selectedYearIndex;
    const maxDay = getDaysInMonth(selectedMonth, currentYear);
    const day = Math.min(selectedDay, maxDay);
    const date = new Date(currentYear, selectedMonth, day);
    const isYearPicked = yearOptional ? selectedYearIndex > 0 : true;
    setYearSelected(isYearPicked);
    onChange(date, isYearPicked);
  }, [selectedDay, selectedMonth, selectedYearIndex]);

  const canDone = yearRequired ? yearSelected : true;

  return (
    <div className="rounded-2xl bg-white shadow-md p-4 space-y-4">
      <div className="flex gap-2" style={{ height: VISIBLE_ITEMS * ITEM_HEIGHT }}>
        <ScrollColumn
          items={dayItems}
          selectedIndex={selectedDay - 1}
          onSelect={(i) => setSelectedDay(i + 1)}
        />
        <ScrollColumn
          items={monthItems}
          selectedIndex={selectedMonth}
          onSelect={(i) => setSelectedMonth(i)}
        />
        <ScrollColumn
          items={yearItems}
          selectedIndex={selectedYearIndex}
          onSelect={(i) => setSelectedYearIndex(i)}
          disabled={false}
        />
      </div>

      {yearOptional && (
        <p className="text-xs text-muted-foreground text-center">
          Year is optional for recurring occasions
        </p>
      )}

      {onDone && (
        <Button
          onClick={onDone}
          disabled={!canDone}
          className="w-full rounded-full bg-[hsl(0_0%_10%)] text-white hover:bg-[hsl(0_0%_10%)]/90"
          size="lg"
        >
          Done
        </Button>
      )}
    </div>
  );
};

export default DateScrollPicker;
